import { Button, Stack, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { useStore } from "zustand";
import type { ParcelSelectionMode, ZoneType } from "@designboticteam/kolega-3d";
import { ZoneTypeSelector } from "@/components/ZoneTypeSelector";
import { useEngineApi } from "@/engine";
import { SelectedParcelsList } from "../SelectedParcelsList";

type Method = "Draw" | "Parcels";

const METHOD_OPTIONS: Array<{ value: Method; label: string }> = [
	{ label: "Draw", value: "Draw" },
	{ label: "Parcels", value: "Parcels" },
];

const SELECTION_MODE_OPTIONS: Array<{ value: ParcelSelectionMode; label: string }> = [
	{ label: "Single parcel", value: "single" },
	{ label: "Multiple (merge)", value: "multiple" },
];

const METHOD_HINTS: Record<Method, string> = {
	Draw: "Click on the map to draw the zone outline. Double-click to finish.",
	Parcels: "Click parcels on the map to add them. The zone is created from their union.",
};

const NewZoneForm = (): React.ReactNode => {
	const api = useEngineApi();
	const selectionMode = useStore(api.kolega3D.store, (store) => store.parcelSelection.mode);
	const selectedParcels = useStore(api.kolega3D.store, (store) => store.parcelSelection.items);

	const [method, setMethod] = useState<Method>("Draw");
	const [zoneType, setZoneType] = useState<ZoneType>(() => api.kolega3D.zones.getDraftType());

	const hasParcelSelection = selectedParcels.length > 0;

	// The form only mounts while ZoneTool is active (see ToolsSection), so the method is just its mode.
	useEffect(() => {
		if (method === "Draw") {
			api.kolega3D.zones.startDrawing();
			return;
		}

		api.kolega3D.zones.startPicking();
	}, [method, api]);

	// Clear parcel previews whenever the form closes (also when another tool is activated elsewhere).
	useEffect(() => {
		return () => {
			api.kolega3D.parcels.clearSelection();
		};
	}, [api]);

	const handleZoneTypeChange = (nextZoneType: ZoneType): void => {
		setZoneType(nextZoneType);
		api.kolega3D.zones.setDraftType(nextZoneType);
	};

	return (
		<Stack direction="column" sx={{ gap: 2 }}>
			<Stack direction="column" sx={{ gap: 0.5 }}>
				<Typography variant="subtitle2">Type</Typography>
				<ZoneTypeSelector value={zoneType} onChange={handleZoneTypeChange} />
			</Stack>

			<Stack direction="column" sx={{ gap: 0.5 }}>
				<Typography variant="subtitle2">Method</Typography>
				<ToggleButtonGroup
					exclusive
					fullWidth
					value={method}
					onChange={(_, nextValue: Method | null) => {
						if (nextValue !== null) {
							setMethod(nextValue);
						}
					}}
				>
					{METHOD_OPTIONS.map(({ label, value }) => (
						<ToggleButton key={value} value={value} size="small">
							<Typography variant="body2" sx={{ textTransform: "none" }}>
								{label}
							</Typography>
						</ToggleButton>
					))}
				</ToggleButtonGroup>
			</Stack>

			<Typography variant="caption" color="textSecondary">
				{METHOD_HINTS[method]}
			</Typography>

			{method === "Parcels" && (
				<Stack direction="column" sx={{ gap: 1 }}>
					<Stack direction="column" sx={{ gap: 0.5 }}>
						<Typography variant="subtitle2">Selection</Typography>
						<ToggleButtonGroup
							exclusive
							fullWidth
							value={selectionMode}
							onChange={(_, nextValue: ParcelSelectionMode | null) => {
								if (nextValue !== null) {
									api.kolega3D.parcels.setSelectionMode(nextValue);
								}
							}}
						>
							{SELECTION_MODE_OPTIONS.map(({ label, value }) => (
								<ToggleButton key={value} value={value} size="small">
									<Typography variant="body2" sx={{ textTransform: "none" }}>
										{label}
									</Typography>
								</ToggleButton>
							))}
						</ToggleButtonGroup>
					</Stack>

					<Stack direction="row" sx={{ alignItems: "center", justifyContent: "space-between" }}>
						<Typography variant="overline">Selected · {selectedParcels.length}</Typography>
						<Button variant="text" size="small" color="mono" onClick={() => api.kolega3D.parcels.clearSelection()}>
							Clear
						</Button>
					</Stack>

					<SelectedParcelsList />

					<Button
						variant="outlined"
						color="mono"
						size="small"
						fullWidth
						disabled={hasParcelSelection === false}
						onClick={() => api.kolega3D.parcels.createZone({ zoneType })}
					>
						Create
					</Button>
				</Stack>
			)}
		</Stack>
	);
};

export { NewZoneForm };
